import { mdiCollapseAll, mdiExpandAll } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Stack } from '@mui/material';
import i18next from 'i18next';
import React from 'react';
import type { IIndexable } from '../../../../types';
import CollapseComponent from '../Components/CollapseComponent';

import styles from './Layers.module.scss';
import type { Core } from '@pdftron/webviewer';
import { ApryseSDK } from '../../../../pdf/ApryseSDK';

const updateExpanded = (
    expanded: IIndexable<boolean>,
    layerNodes: Core.Document.LayerContext[],
    value = true,
    nestLvl = 0,
) => {
    if (!layerNodes.length) return;
    layerNodes.forEach((node, idx) => {
        if (!node.obj) {
            node.obj = `${nestLvl}_${idx}`;
        }
        expanded[node.obj] = value;
        updateExpanded(expanded, [...node.children], undefined, nestLvl++);
    });
};

export default () => {
    // Random value to guarantee a refresh even if toggleRefreshState gets called multiple times (even ones) in a row
    // A boolean value would not change if toggleRefreshState is called 2, 4, 6, ... times in a row
    const [refreshState, toggleRefreshState] = React.useReducer(() => Math.random(), 0);

    // Force a no argument method, otherwise warnings will appear, because arguments are passed from event handlers.
    const forceRefresh = React.useCallback(() => toggleRefreshState(), []);

    const layerNodes = React.useRef<Core.Document.LayerContext[]>([]);
    const expanded = React.useRef<IIndexable<boolean>>({});

    React.useEffect(() => {
        ApryseSDK.doc.getLayersArray().then(layers => {
            layerNodes.current = layers;
            updateExpanded(expanded.current, layerNodes.current);
            forceRefresh();
        });
    }, []);

    const expandAll = () => {
        Object.keys(expanded.current).forEach(key => {
            expanded.current[key] = true;
        });
        forceRefresh();
    };

    const collapseAll = () => {
        Object.keys(expanded.current).forEach(key => {
            expanded.current[key] = false;
        });
        forceRefresh();
    };

    const toggleExpanded = (key: string) => {
        expanded.current[key] = !expanded.current[key];
        forceRefresh();
    };

    const onLayersChanged = () => {
        ApryseSDK.doc.setLayersArray(layerNodes.current);
        ApryseSDK.documentViewer.refreshAll(); // clear page cache
        ApryseSDK.documentViewer.updateView(); // redraw page

        forceRefresh(); // refresh UI
    };

    /*
    const onCheckAll = () => {
        //ApryseSDK.documentViewer.getDocument();
        //forceRefresh();
    };

    const onUncheckAll = () => {
        //Object.keys(checked.current).forEach(key => {
        //    checked.current[key] = false;
        //});
        //forceRefresh();
    };
    */

    const menu = React.useMemo(() => {
        return (
            <Stack className={styles.sidebarMenuArea} direction="row" spacing={1} pl={2} pr={2}>
                {/*
                <Button
                    className={styles.iconButton}
                    variant="outlined"
                    key='expand'
                    title={i18next.t('sidebar.popups.layers.checkAll')}
                    onClick={onCheckAll}
                >
                    <Icon path={mdiCheckboxMultipleMarked} />
                </Button>
                <Button
                    className={styles.iconButton}
                    variant="outlined"
                    key='collapse'
                    title={i18next.t('sidebar.popups.layers.uncheckAll')}
                    onClick={onUncheckAll}
                >
                    <Icon path={mdiCheckboxMultipleBlank} />
                </Button>
                */}
                <Button
                    className={styles.iconButton}
                    variant="outlined"
                    key="expand"
                    title={i18next.t('sidebar.popups.layers.expand')}
                    onClick={() => expandAll()}
                >
                    <Icon path={mdiExpandAll} />
                </Button>
                <Button
                    className={styles.iconButton}
                    variant="outlined"
                    key="collapse"
                    title={i18next.t('sidebar.popups.layers.collapse')}
                    onClick={() => collapseAll()}
                >
                    <Icon path={mdiCollapseAll} />
                </Button>
            </Stack>
        );
    }, []);

    const renderLayers = React.useMemo(() => {
        return !layerNodes.current?.length ? (
            <></>
        ) : (
            <CollapseComponent
                level={0}
                layerNodes={layerNodes.current}
                expanded={expanded.current}
                altTitle={i18next.t('sidebar.title.layer') + ' 1'}
                toggleExpanded={toggleExpanded}
                onLayersChanged={onLayersChanged}
            />
        );
    }, [refreshState]);

    return (
        <>
            {menu}
            {renderLayers}
        </>
    );
};
